import {
  CAMPAIGN_PLACEMENT_LINES,
  formatPlacementBidPercent,
  parseCampaignPlacementBidPercents,
  type CampaignPlacementBidKey,
} from '@/utils/workspaceCampaignPlacement';
import { classifyPlacement } from '@/utils/workspacePlacementMetrics';
import type { WorkspaceCampaignPlacementKey } from '@/api/workspaceCampaignPlacementTypes';
import type { CampaignAdProduct } from '@/types/spCampaigns';

/** Matches backend `services.campaign_placement.PLACEMENT_MIN` / `PLACEMENT_MAX`. */
export const PLACEMENT_BID_PERCENT_MIN = -99;
export const PLACEMENT_BID_PERCENT_MAX = 900;

const BID_KEY_TO_API: Record<CampaignPlacementBidKey, WorkspaceCampaignPlacementKey> = {
  top_of_search: 'TOP_OF_SEARCH',
  rest_of_search: 'REST_OF_SEARCH',
  product_pages: 'PRODUCT_PAGES',
};

export type CampaignPlacementTableRow = {
  /** Stable key for React lists. */
  id: string;
  /** Human-readable placement name shown in the table. */
  label: string;
  /** Numeric bid adjustment %, or null when unknown. */
  percentage: number | null;
  /** Formatted display like `50%` or `—`. */
  percentageLabel: string;
  /**
   * Internal API placement key when this row can be updated via the existing
   * workspace placement-jobs endpoint. Null for read-only rows
   * (e.g. SITE_AMAZON_BUSINESS) or unsupported ad products (SD).
   */
  editableKey: WorkspaceCampaignPlacementKey | null;
  readOnlyReason: string | null;
};

function placementDisplayLabel(placement: unknown): string {
  const bucket = classifyPlacement(placement);
  if (bucket === 'top_of_search') return 'Top of Search';
  if (bucket === 'rest_of_search') return 'Rest of Search';
  if (bucket === 'product_pages') return 'Product Pages';
  if (bucket === 'homepage') return 'Homepage on-Amazon';
  if (placement == null || placement === '') return '—';
  return String(placement);
}

function editableKeyForPlacement(
  placement: unknown,
  adProduct: CampaignAdProduct
): { key: WorkspaceCampaignPlacementKey | null; reason: string | null } {
  if (adProduct === 'SD') {
    return {
      key: null,
      reason: 'Placement bid adjustments are not supported for Sponsored Display',
    };
  }
  if (adProduct !== 'SP' && adProduct !== 'SB') {
    return { key: null, reason: 'Placement bid adjustments are not supported for this ad product' };
  }

  const token = String(placement ?? '').trim().toUpperCase();
  if (token.includes('BUSINESS') || token.includes('SITE_AMAZON')) {
    return {
      key: null,
      reason: 'Amazon Business placement adjustments are not editable yet',
    };
  }

  const bucket = classifyPlacement(placement);
  if (bucket == null || bucket === 'homepage') {
    return {
      key: null,
      reason: 'This placement cannot be updated with the current API',
    };
  }
  return { key: BID_KEY_TO_API[bucket], reason: null };
}

function readPercentage(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

/**
 * Build Placements table rows from campaign raw payload.
 * Prefer Amazon placement arrays so unknown tokens (e.g. SITE_AMAZON_BUSINESS) stay visible.
 */
export function extractCampaignPlacementRows(
  raw: Record<string, unknown>,
  adProduct: CampaignAdProduct
): CampaignPlacementTableRow[] {
  const rows: CampaignPlacementTableRow[] = [];
  const dynamicBidding =
    raw.dynamicBidding && typeof raw.dynamicBidding === 'object'
      ? (raw.dynamicBidding as Record<string, unknown>)
      : null;
  const bidding =
    raw.bidding && typeof raw.bidding === 'object'
      ? (raw.bidding as Record<string, unknown>)
      : null;

  const sources = [dynamicBidding?.placementBidding, bidding?.bidAdjustmentsByPlacement];

  for (const source of sources) {
    if (!Array.isArray(source)) continue;
    for (const item of source) {
      if (!item || typeof item !== 'object') continue;
      const rec = item as Record<string, unknown>;
      const rawPlacement = rec.placement == null ? null : String(rec.placement);
      const percentage = readPercentage(rec.percentage);
      const { key, reason } = editableKeyForPlacement(rawPlacement, adProduct);
      rows.push({
        id: `${rawPlacement ?? 'unknown'}-${rows.length}`,
        label: placementDisplayLabel(rawPlacement),
        percentage,
        percentageLabel: percentage == null ? '—' : `${percentage}%`,
        editableKey: key,
        readOnlyReason: key ? null : reason,
      });
    }
  }

  if (rows.length > 0) return rows;

  const percents = parseCampaignPlacementBidPercents(raw);
  return CAMPAIGN_PLACEMENT_LINES.filter((line) => percents[line.id] != null).map((line, index) => {
    const percentage = percents[line.id];
    const { key, reason } = editableKeyForPlacement(line.id, adProduct);
    return {
      id: `${line.id}-${index}`,
      label: line.label,
      percentage,
      percentageLabel: formatPlacementBidPercent(percentage),
      editableKey: key,
      readOnlyReason: key ? null : reason,
    };
  });
}

export function validatePlacementBidPercent(raw: string): string | null {
  const trimmed = raw.trim();
  if (trimmed === '') return 'Enter a bid adjustment percentage';
  const numeric = Number(trimmed);
  if (!Number.isFinite(numeric)) return 'Enter a valid number';
  if (!Number.isInteger(numeric)) return 'Enter a whole number percentage';
  if (numeric < PLACEMENT_BID_PERCENT_MIN || numeric > PLACEMENT_BID_PERCENT_MAX) {
    return `Percentage must be between ${PLACEMENT_BID_PERCENT_MIN} and ${PLACEMENT_BID_PERCENT_MAX}`;
  }
  return null;
}
